// src/ui/Logo.tsx (T1): the builder's logo from the tenant (brand.logo), left-aligned everywhere (docs/DECISIONS.md),
// sized optically (shared/logo.ts, docs/design/system/tokens-a.md §1). Each slot is a box from the --logo-* tokens:
// "side" (the sidebar, the footer, the sign-in panel), "bar" (the phone header) and "door" (Start Your Build and the
// demo key on desktop; a stylesheet moves a slot to the bar box on a phone). Inside its box a logo gets the area of a
// square 1.2 times the box's height, so a wide wordmark and a compact badge carry about the same weight. The element
// carries the logo's aspect (--logo-ar) and its pixel size in the box, so nothing moves when the image loads.
// A new builder's starter badge (shared/logo.ts isStarterLogo) shows the company name as text (.is-text) in the same box.
// Root class: hb-logo.
import { useLayoutEffect, useRef } from "preact/hooks";
import { isStacked, isStarterLogo, LOGO_BOXES, logoAspect, logoSize, type LogoSlot } from "../../shared/logo";
import { tenant } from "../state/session";
import { cx } from "./cx";

/** The smallest a text wordmark gets before it may take a second line (every slot). */
export const TEXT_LOGO_MIN_PX = 15;

/**
 * Fits a text wordmark in its box: the font shrinks from its own size down to `min` px until the name fits on one line;
 * if it still does not, it stays at `min` and may take two lines (is-wrap). Returns the size it settled on.
 */
export function fitTextLogo(el: HTMLElement, min = TEXT_LOGO_MIN_PX): number {
  el.style.fontSize = "";
  el.classList.remove("is-wrap");
  const base = parseFloat(getComputedStyle(el).fontSize) || min;
  const fits = () => el.scrollWidth <= el.clientWidth + 0.5;
  if (fits() || base <= min) return base;
  for (let px = Math.floor(base * 2) / 2; px >= min; px -= 0.5) {
    el.style.fontSize = `${px}px`;
    if (fits()) return px;
  }
  el.style.fontSize = `${min}px`;
  el.classList.add("is-wrap");
  return min;
}

function TextLogo(props: { name: string; slot: LogoSlot; onDark?: boolean; class?: string; decorative?: boolean }) {
  const ref = useRef<HTMLSpanElement>(null);
  // Every slot fits its box (the sidebar, the board and team headers, Start Your Build's door, sign-in). A stylesheet moves a
  // slot to a different box on a phone, so the name is fitted again when the window changes.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const refit = () => fitTextLogo(el);
    refit();
    // The heading font may arrive after the first paint and change the name's width.
    let gone = false;
    void document.fonts?.ready.then(() => !gone && refit());
    window.addEventListener("resize", refit);
    return () => {
      gone = true;
      window.removeEventListener("resize", refit);
    };
  }, [props.name, props.slot]);
  return (
    <span
      ref={ref}
      class={cx("hb-logo", "is-text", `is-${props.slot}`, props.onDark && "on-dark", props.class)}
      {...(props.decorative ? { "aria-hidden": "true" } : {})}
    >
      {props.name}
    </span>
  );
}

export function Logo(props: { slot?: LogoSlot; onDark?: boolean; class?: string; decorative?: boolean }) {
  const t = tenant.value;
  if (!t) return null;
  const slot = props.slot ?? "side";
  // The starter badge is a blank ink square: until the builder has its own logo, its name stands in the same box.
  if (isStarterLogo(t.brand.logo)) {
    return <TextLogo name={t.name} slot={slot} onDark={props.onDark} class={props.class} decorative={props.decorative} />;
  }
  const ar = logoAspect(t.brand.logo);
  const size = logoSize(ar, LOGO_BOXES[slot]);
  return (
    <img
      class={cx("hb-logo", `is-${slot}`, isStacked(ar) && "is-stacked", props.class)}
      src={props.onDark ? t.brand.logo.onDark : t.brand.logo.onLight}
      alt={props.decorative ? "" : t.brand.logo.alt}
      width={size.width}
      height={size.height}
      style={{ "--logo-ar": ar.toFixed(4) }}
    />
  );
}
